import { useEffect } from 'react'

import request from 'api'
import { putData, useAppDispatch, useAppSelector } from 'store'

export interface FamilyMember {
  key: string
  familyName: string
  givenName: string
  loginId: string
  age: number
  role: 'OWNER' | 'MEMBER' | 'CHILD'
  isMe: boolean
  imageUrl?: string
}

export interface FamilyInvitation {
  invitee: string
  invitationTime: number
  invitationId: string
}

interface FamilyMembersResponse {
  members: FamilyMember[]
}

const familyMembersResponseMock: FamilyMembersResponse = {
  members: []
}

interface FamilyInvitationsResponse {
  invitations: FamilyInvitation[]
}

const familyInvitationsResponseMock: FamilyInvitationsResponse = {
  invitations: []
}

interface ParentalControlResponse {
  spcEnabled: boolean
}

const parentalControlResponseMock: ParentalControlResponse = {
  spcEnabled: false
}

interface Family {
  members: FamilyMember[]
  memberCount: number
  isChild: boolean
  isOwner: boolean
  ownerAccount: string
  ownerGivenName: string
  ownerFamilyName: string
  ownerImageUrl: string
  invitations: FamilyInvitation[]
  spcEnabled: boolean
}

const familyMock = {
  members: [] as FamilyMember[],
  memberCount: 0,
  ownerAccount: '',
  invitations: [] as FamilyInvitation[]
} as Family

export function useFamily(forceFetch = false) {
  const family = useAppSelector((state) => state.data.family as Family)
  const dispatch = useAppDispatch()
  const confirmPasswordKey = useAppSelector((state) => state.confirmPassword.key)

  const fetchFamily = async () => {
    const familyMembers = await request
      .get<FamilyMembersResponse>('/v1/families/members', { headers: { 'x-confirm-key': confirmPasswordKey } })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Failed to fetch family members')
        }
        return {
          members: response.data.members || []
        } as FamilyMembersResponse
      })
      .catch(() => {
        return familyMembersResponseMock
      })

    const familyInvitations = await request
      .get<FamilyInvitationsResponse>('/v1/families/invitations', { headers: { 'x-confirm-key': confirmPasswordKey } })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Failed to fetch family invitations')
        }
        return {
          invitations: response.data.invitations || []
        } as FamilyInvitationsResponse
      })
      .catch(() => {
        return familyInvitationsResponseMock
      })

    const sortedMembers = familyMembers.members.sort((member1, member2) => {
      if (member1.isMe) {
        return -1
      }
      if (member2.isMe) {
        return 1
      }
      if (member1.role === 'OWNER') {
        return -1
      }
      if (member2.role === 'OWNER') {
        return 1
      }
      if (member1.role === 'MEMBER' && member2.role === 'CHILD') {
        return -1
      }
      if (member1.role === 'CHILD' && member2.role === 'MEMBER') {
        return 1
      }

      if (member1.role === member2.role) {
        const ageComparison = member2.age - member1.age
        if (ageComparison !== 0) return ageComparison

        const givenNameComparison = member1.givenName.localeCompare(member2.givenName)
        if (givenNameComparison !== 0) return givenNameComparison

        return member1.familyName.localeCompare(member2.familyName)
      }

      return member1.givenName.localeCompare(member2.givenName)
    })

    const sortedInvitations = familyInvitations.invitations.sort(
      (invitation1, invitation2) => invitation1.invitationTime - invitation2.invitationTime
    )

    const owner = familyMembers.members.find((member) => member.role === 'OWNER')

    const result = {
      members: sortedMembers,
      memberCount: sortedMembers?.length || 0,
      ownerGivenName: owner?.givenName || '',
      ownerFamilyName: owner?.familyName || '',
      ownerImageUrl: owner?.imageUrl || '',
      invitations: sortedInvitations,
      spcEnabled: false
    } as Family

    result.isChild = await familyMembers?.members.some((member) => {
      return member.isMe === true && member.role === 'CHILD'
    })
    result.isOwner = await familyMembers?.members.some((member) => {
      return member.isMe === true && member.role === 'OWNER'
    })
    result.ownerAccount =
      (await familyMembers?.members.find((member) => {
        return member.role === 'OWNER'
      })?.loginId) || ''

    if (result.memberCount > 0 && !result.isOwner && !result.isChild) {
      const parentalControl = await request
        .get<ParentalControlResponse>(`/parental-control`)
        .then((response) => {
          if (response.status !== 200) {
            throw new Error('Failed to fetch spc')
          }
          return {
            spcEnabled: response.data.spcEnabled
          } as ParentalControlResponse
        })
        .catch(() => {
          return parentalControlResponseMock
        })

      result.spcEnabled = parentalControl.spcEnabled
    }

    dispatch(putData({ memberCount: result.memberCount }))
    dispatch(putData({ family: result }))

    return result
  }

  useEffect(() => {
    if (forceFetch || family === undefined) {
      fetchFamily()
    }
  }, [])

  return { family: family === undefined ? familyMock : family, isInitializing: family === undefined, fetchFamily }
}
